import {
  SalaryDistributionAnalyticalFeatureSettingsDto,
  type SalaryDistributionAnalyticalFeatureSettingsInterface
} from '@zaoblako/shared/modules/document/dtos/salary-distribution/form/salary-distribution-analytical-feature-settings.dto';
import {
  SalaryDistributionUserFormDto,
  type SalaryDistributionUserFormInterface
} from '@zaoblako/shared/modules/document/dtos/salary-distribution/form/salary-distribution-user-form.dto';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { DocumentDtoKeysEnum } from '@zaoblako/shared/modules/document/enums/document-dto-keys.enum';
import { DtoMessageEnum } from '@zaoblako/shared/common/enums/dto.message.enum';
import { CurrentTimeZoneExtractorClass } from '@zaoblako/shared/modules/dates/classes/current-time-zone-extractor.class';
import { DocumentTypeCodeValidateTools } from '@zaoblako/shared/modules/document/classes/document.type.code.validate.tools';
import { DocumentCheckersEnum } from '@zaoblako/shared/modules/document/enums/document-checkers.enum';
import { ClientCheckerKeysEnum } from '@zaoblako/shared/modules/client/enums/client-checker-keys.enum';
import { CatalogCheckersEnum } from '@zaoblako/shared/modules/catalog/enums/catalog-checkers.enum';
import { entityLikeSchema } from '@zaoblako/shared/modules/serde/schemas/entity-like.schema';
import type { DocumentTypeFindInterface } from '@zaoblako/shared/modules/document/dtos/document-type/document-type-find.dto';
import type { OperationTypeFindInterface } from '@zaoblako/shared/modules/document/dtos/operation-type/operation-type-find.dto';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import type { CurrencyFindInterface } from '@zaoblako/shared/modules/catalog/dtos/currency/currency-find.dto';

import * as yup from 'yup';
import * as moment from 'moment';
import { isNil, negate } from 'lodash';

const isNotNil = negate(isNil);

/** Salary distribution settings create request body */
export interface SaleDistributionFormInterface {
  // Data selection settings
  /** Selection period start date */
  from: Date;
  /** Selection period end date */
  to: Date;
  /** Salary distribution settings */
  settings: SalaryDistributionAnalyticalFeatureSettingsInterface;
  // Document settings
  /** Document date to set */
  date: Date;
  /** Document company to set */
  company: CompanyFindInterface;
  /** Document operation type to set */
  operationType?: OperationTypeFindInterface;
  /** Type of document to create */
  documentType: DocumentTypeFindInterface;
  /** Document currency to set */
  currency?: CurrencyFindInterface;
  /** Users salary settings */
  salaryDistributionUsers: SalaryDistributionUserFormInterface[];
}

/** Salary distribution settings create request body - DTO schema */
export const SaleDistributionFormDto = SerdeDtoClass.create<SaleDistributionFormInterface>(DocumentDtoKeysEnum.SALARY_DISTRIBUTION_FORM_DTO, {
  from: {
    label: 'Начало периода',
    serializer: yup
      .date()
      .required(DtoMessageEnum.REQUIRED_FIELD)
      .transform(
        value => moment
          .tz(value, CurrentTimeZoneExtractorClass.getInstance().getCurrentTimeZone())
          .startOf('day')
          .toDate(),
      ),
  },
  to: {
    label: 'Конец периода',
    serializer: yup
      .date()
      .required()
      .transform(
        value => moment
          .tz(value, CurrentTimeZoneExtractorClass.getInstance().getCurrentTimeZone())
          .endOf('day')
          .toDate()
      ),
  },
  settings: {
    label: 'Настройки аналитических признаков',
    nested: SalaryDistributionAnalyticalFeatureSettingsDto,
    serializer: (_, nested) => nested.test({
      message: 'Настройки должны быть заданы хотя-бы для одного признака',
      test: value => (
        value.direction?.distribute ||
        isNotNil(value.direction?.target?.id) ||
        value.unit?.distribute ||
        isNotNil(value.unit?.target?.id) ||
        value.filial?.distribute ||
        isNotNil(value.filial?.target?.id)
      ),
    }),
  },
  date: {
    label: 'Дата документа',
    serializer: yup
      .date()
      .required()
      .transform(
        value => moment
          .tz(value, CurrentTimeZoneExtractorClass.getInstance().getCurrentTimeZone())
          .startOf('day')
          .toDate(),
      ),
  },
  company: {
    label: 'Компания',
    checkers: [ClientCheckerKeysEnum.COMPANY_ASSERT_EXISTS_BY_DTO],
    serializer: entityLikeSchema,
  },
  operationType: {
    optional: true,
    label: 'Вид операции',
    checkers: [DocumentCheckersEnum.DOCUMENT_TYPE_ASSERT_EXISTS_BY_DTO],
    serializer: entityLikeSchema,
  },
  documentType: {
    label: 'Тип документа',
    checkers: [DocumentCheckersEnum.DOCUMENT_TYPE_ASSERT_EXISTS_BY_DTO],
    serializer: yup.object({
      code: DocumentTypeCodeValidateTools.getInstance().yupSchema,
    }) as yup.Schema<any>,
  },
  currency: {
    optional: true,
    label: 'Валюта',
    serializer: entityLikeSchema,
    checkers: [CatalogCheckersEnum.CURRENCY_ASSERT_EXISTS_BY_DTO],
  },
  salaryDistributionUsers: {
    label: 'Сотрудники',
    nested: SalaryDistributionUserFormDto,
    serializer: (_, nested) => yup
      .array(nested)
      .min(1, DtoMessageEnum.EMPTY_ARRAY),
  },
});
